// Import material ui modules.
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	useFormMortgageeBase,
	UseFormMortgageeBaseParameters,
	UseFormMortgageeBaseResults,
} from '@app/hooks/view';
// Import props.
import {
	FormPopupMortgageeProps,
	FormPopupProps,
	NavigationFooterProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
    PopupWidth,
} from '@app/enumerations';

type UseFormPopupMortgageeParameters = UseFormMortgageeBaseParameters<FormPopupMortgageeProps>;

interface UseFormPopupMortgageeResults extends UseFormMortgageeBaseResults {
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupMortgagee = (params: UseFormPopupMortgageeParameters): UseFormPopupMortgageeResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: params.props.popupTitle,
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: isFormPopupOpen(),
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods

	// #region private methods

	const isFormPopupOpen = (): boolean => {
		return params.props.isOpen
			&& !addressVerification.isSingleLineAddressVerificationRequired()
			&& !addressVerification.isAddressVerificationRequired();
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	//#endregion id generators

	// #endregion private methods


	// #region variables

	const {
		submitForm,
		addressVerification,
		...formMortgageeBase
	} = useFormMortgageeBase(params);

	// #endregion variables


	return {
		applyFormProps,
		applyNavigationFooterProps,
		submitForm,
		addressVerification,
		...formMortgageeBase,
	};
}

// Export parameters.
export {
	UseFormPopupMortgageeParameters,
}

// Export results.
export {
	UseFormPopupMortgageeResults,
}

export default useFormPopupMortgagee;